import { useCallback } from 'react'
import { TKeys } from './types'
import {
    ALL_LANGUAGES,
    ALL_LANGUAGES_TYPE,
    AR_DEFAULT,
    DEFAULT_LOCAL,
    LOCAL_MAP,
} from './constants'
import { OptionsType } from '../components/BaseSelect'
import { en_en } from './en-en'
import { FullParameters } from '../parameters/types'
import { CommonValueType } from '../context/types'
import { useCommonCtx } from '../context/useCommonCtx'
import { DEPRECATED_KEYS, DEPRECATED_TO_BCP47 } from './bcp47toLegacy'

export const AllLanguagesOptions: OptionsType<number> = ALL_LANGUAGES.map(
    (ln, v) => ({ v, k: `${LOCAL_MAP[ln].Flag} ${LOCAL_MAP[ln].label}` })
)
//FIXME use local as key instead of index
export const langIndex2AppLang = (langIndex: number) => ALL_LANGUAGES[langIndex]

export const appLangFromStr = (local?: string): ALL_LANGUAGES_TYPE => {
    const lowerCasedLocal = local?.toLowerCase()
    // FIXME @deprecated
    const normalized =
        lowerCasedLocal && lowerCasedLocal in DEPRECATED_TO_BCP47
            ? DEPRECATED_TO_BCP47[lowerCasedLocal as DEPRECATED_KEYS]
            : lowerCasedLocal
    if (normalized && normalized in LOCAL_MAP) {
        return normalized as ALL_LANGUAGES_TYPE
    }
    if (normalized && (normalized === 'ar' || normalized.startsWith('ar-'))) {
        return AR_DEFAULT as ALL_LANGUAGES_TYPE
    }
    return DEFAULT_LOCAL as ALL_LANGUAGES_TYPE
}

export const indexFromLang = (lang: ALL_LANGUAGES_TYPE) =>
    ALL_LANGUAGES.indexOf(lang)

export const langIndexFromLocal = (local?: string) =>
    indexFromLang(appLangFromStr(local))

/**
 * Returns a new array with unique strings from the input array, removing duplicates.
 * @example
 * uniqStrings(['en-uk', 'en-gb', 'fr-fr']) // ['en-gb', 'fr-fr'] (en-uk → en-gb, duplicates removed)
 */
export const uniqStrings = (strs?: readonly string[] | undefined) =>
    Array.from(new Set(strs ?? []))

/**
 * @deprecated
 * FIXME to handle DEPRECATED_LANG_ALIASES
 * Resolves brandText1/brandText2 value when the record may contain legacy locale keys (e.g. remote API sends en-en instead of en-us).
 * Tries canonical key first, then any deprecated alias that maps to canonicalLang.
 */
export const getBrandTextByLang = (
    record: Partial<Record<string, string>> | undefined,
    canonicalLang: ALL_LANGUAGES_TYPE
): string | undefined => {
    if (!record) return undefined
    const direct = record[canonicalLang]
    if (direct !== undefined && direct !== '') return direct
    for (const [legacy, canon] of Object.entries(DEPRECATED_TO_BCP47))
        if (canon === canonicalLang) {
            const v = record[legacy]
            if (v !== undefined && v !== '') return v
        }
    return undefined
}

export type TranslatorFn = (key?: TKeys, vars?: string[]) => string

const VAR_IN = '{{v'
const VAR_OUT = '}}'
export const getTranslator =
    (langIndex: number): TranslatorFn =>
    (key?: TKeys, vars?: string[]): string => {
        // For debug purpose, override langIndex to 0 (fr) here
        if (!key) {
            return ''
        }
        const fallback = en_en[key] ?? key
        const { i18n } = LOCAL_MAP[langIndex2AppLang(langIndex)]
        const translation = i18n[key] ?? fallback
        return vars?.length
            ? vars.reduce((result, value, index) => {
                  // FIXME use a better regex as constant,
                  // instead of using a new RegExp each time
                  const pattern = new RegExp(
                      `${VAR_IN}${index + 1}${VAR_OUT}`,
                      'g'
                  )
                  return result.replace(pattern, value)
              }, translation)
            : translation
    }

// export const useTranslation = (langIndex: number) =>
//     useCallback(getTranslator(langIndex), [langIndex])

export const useTranslation = (common?: CommonValueType) => {
    const commonCtx = useCommonCtx()
    const { state } = common ?? commonCtx
    const { langIndex } = state.params.assessmentParams
    return useCallback(getTranslator(langIndex), [langIndex])
}

export const isArabic = (params: FullParameters) => {
    const lang = ALL_LANGUAGES[params.assessmentParams.langIndex]
    return lang === 'ar' || lang.startsWith('ar-')
}

const BOLD_REGEX = /\*\*([^\*]+)\*\*/g
const BOLD_CONTENT_REGEX = /\*\*/g
const BOLD_CONTENT_TOKEN = '**'
type BoldChunk = { string: string; bold: boolean }

export const toBoldStrings = (s: string) =>
    s
        .split(BOLD_CONTENT_TOKEN)
        //.filter(s => !!s?.length)
        .map(replaceFirstOrLastWithNbsp)

// KO, prevent the normal white space wrap on overflow on mobile...
// TODO encapsulate in a component with proper wrapping container and style
export const toBoldStringsNbsp = (s: string) =>
    replaceSpacesWithNbsp(s).split(BOLD_CONTENT_TOKEN)

const replaceSpacesWithNbsp = (s: string): string => s.replace(/ /g, '\u00A0')

const replaceFirstOrLastWithNbsp = (s: string): string => {
    const first = s?.[0] === ' ' ? '\u00A0' : s?.[0] ?? ''
    const last = s?.[s.length - 1] === ' ' ? '\u00A0' : s?.[s.length - 1] ?? ''
    return `${first}${s.slice(1, s.length - 1)}${last}`
}
